import React, { useState } from "react";
import Notification from "../../components/UI/Notification";

const UploadResumes = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [downloadLink, setDownloadLink] = useState("");
  const [notification, setNotification] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.name.endsWith(".zip")) {
      setFile(selectedFile);
    } else {
      setFile(null);
      setNotification({
        message: "Please upload a valid ZIP file.",
        type: "error",
      });
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setNotification({
        message: "Please select a file to upload.",
        type: "error",
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        "https://hrmsnew.prepstripe.com/api/upload-zip",
        {
          method: "POST",
          body: formData,
        },
      );

      const result = await response.json();

      if (
        response.ok &&
        result.message ===
          "ZIP file uploaded and resumes extracted successfully."
      ) {
        setNotification({
          message: "File uploaded and extracted successfully!",
          type: "success",
        });
        setCurrentStep(2); // Move to step 2
      } else {
        setNotification({
          message: "File upload failed.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);

      setNotification({
        message: "An error occurred during the upload.",
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const processResumes = async () => {
    setProcessing(true);

    try {
      const response = await fetch(
        "https://hrmsnew.prepstripe.com/api/process-resumes",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            extracted_directory:
              "https://hrmspayroll.prepstripe.com/storage/resumes",
          }),
        },
      );

      const result = await response.json();

      if (response.ok) {
        // alert(result.message);
        // setNotification(result.message);
        setNotification({
          message: result.message,
          type: "success",
        });
        setDownloadLink(result.file_url); // Save the download link
        setCurrentStep(3); // Move to step 3
      } else {
        // alert("Failed to process resumes.");
        // setNotification("Failed to process resumes.");
        setNotification({
          message: "Failed to process resumes.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error processing resumes:", error);
      // alert("An error occurred during resume processing.");
      // setNotification("An error occurred during resume processing.");
      setNotification({
        message: "An error occurred during resume processing.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white dark:bg-black flex items-center justify-center p-2 ">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="bg-white dark:bg-gray-900 p-6 sm:p-10 font-poppins rounded-xl w-full max-w-5xl">
        <h2 className="text-2xl sm:text-3xl font-semibold dark:text-white text-blue-600 mb-6 sm:mb-8 text-center">
          Upload and Process Resumes
        </h2>

        {/* Stepper */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-1 mb-8">
          <div
            className={`flex-1 flex items-center justify-center py-3 px-4 rounded-full shadow-md ${
              currentStep === 1
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            <span className="font-semibold text-center text-sm sm:text-base">
              1. Upload ZIP
            </span>
          </div>
          <div className="w-full sm:h-1 sm:mx-2 bg-gray-300 sm:w-auto sm:flex-1" />
          <div
            className={`flex-1 flex items-center justify-center py-3 px-6 rounded-full shadow-md ${
              currentStep === 2
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            <span className="font-semibold text-center text-sm sm:text-base">
              2. Process Resumes
            </span>
          </div>
          <div className="w-full sm:h-1 sm:mx-2 bg-gray-300 sm:w-auto sm:flex-1" />
          <div
            className={`flex-1 flex items-center justify-center py-3 px-4 rounded-full shadow-md ${
              currentStep === 3
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            <span className="font-semibold text-center text-sm sm:text-base">
              3. Download Excel
            </span>
          </div>
        </div>

        {/* Step 1: Upload ZIP */}
        {currentStep === 1 && (
          <div className="flex flex-col items-center">
            <label
              htmlFor="file-upload"
              className="block text-base dark:text-white sm:text-lg font-medium text-gray-700 mb-4 text-center"
            >
              Select a ZIP file containing resumes:
            </label>
            <input
              type="file"
              id="file-upload"
              accept=".zip"
              onChange={handleFileChange}
              name="zip_file"
              className="block w-full max-w-xs dark:text-white text-sm text-gray-700 border border-gray-300 rounded-lg p-3 shadow-sm focus:ring-2 focus:ring-blue-500"
            />
            {file && (
              <div className="mt-4 text-gray-600 text-sm">
                <p>
                  <strong>File:</strong> {file.name}
                </p>
                <p>
                  <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)}{" "}
                  MB
                </p>
              </div>
            )}
            <button
              onClick={handleUpload}
              disabled={uploading}
              className={`mt-6 w-full max-w-xs py-3 rounded-lg font-semibold text-white transition-all duration-200 ${
                uploading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {uploading ? "Uploading..." : "Upload ZIP"}
            </button>
          </div>
        )}

        {/* Step 2: Process Resumes */}
        {currentStep === 2 && (
          <div className="text-center">
            <p className="text-base sm:text-lg text-gray-700 mb-6">
              Resumes have been successfully extracted. Click below to process
              them.
            </p>
            <button
              onClick={processResumes}
              disabled={processing}
              className={`w-full max-w-sm py-3 rounded-lg font-semibold text-white transition-all duration-200 ${
                processing
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600"
              }`}
            >
              {processing ? "Processing Resumes..." : "Process Resumes"}
            </button>
          </div>
        )}

        {/* Step 3: Download Excel */}
        {currentStep === 3 && (
          <div className="text-center">
            <p className="text-base sm:text-lg text-gray-700 mb-6">
              Resumes have been processed successfully. You can now download the
              Excel file.
            </p>
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full max-w-sm inline-block py-3 px-6 rounded-lg font-semibold text-white bg-blue-500 hover:bg-blue-600 transition-all duration-200 shadow-md"
            >
              Download Excel File
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadResumes;
